import {
  Grid,
  Box,
  IconButton,
  Button,
  Typography,
  Skeleton,
  useMediaQuery,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LinkIcon from "@mui/icons-material/Link";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import AssignmentLateRoundedIcon from "@mui/icons-material/AssignmentLateRounded";

import { useEffect, useState, useCallback } from "react";
import DatagridCustom from "../General/DatagridCustom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "@mui/material/styles";

import {
  getUnlinkedLawsuits,
  downloadUnlinkedLawsuit,
  updateUnlinkedLawsuit,
  completeUnlinkedLawsuit,
  discardUnlinkedLawsuit,
} from "../../services/UnlinkedLawsuitService";

import AppDialog from "../General/Dialog/AppDialog";
import ConfirmDialog from "../General/Dialog/ConfirmDialog";
import EditDialog from "../General/Dialog/EditDialog";
import { useToast } from "../../context/ToastContext";

const TYPE_OPTIONS = [
  { value: "Automotor", label: "Automotor" },
  { value: "Inmueble", label: "Inmueble" },
  { value: "Comercio e Industria", label: "Comercio e Industria" },
  { value: "Tasa Administrativa", label: "Tasa Administrativa" },
];

const PERSON_TYPE_OPTIONS = [
  { value: "F", label: "Física" },
  { value: "J", label: "Jurídica" },
];

const REQUIRED_FIELD_LABELS = {
  type: "Tipo",
  order_number: "Orden",
  identification: "Identificador",
  decree: "Decreto",
  amount: "Monto",
  date: "Fecha",
};

const emptyEditData = {
  type: "",
  order_number: "",
  identification: "",
  decree: "",
  dni_cuit: "",
  first_name: "",
  last_name: "",
  person_type: "",
  amount: "",
  date: "",
  contribution: "",
  address: "",
  reason: "",
};

export default function ManagerUnlinkedLawsuit() {
  const { showToast } = useToast();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [filterModel, setFilterModel] = useState({
    items: [],
    quickFilterValues: [],
  });

  const [openPreview, setOpenPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [openEdit, setOpenEdit] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [editData, setEditData] = useState(emptyEditData);
  const [linking, setLinking] = useState(false);

  const [openDiscardConfirm, setOpenDiscardConfirm] = useState(false);
  const [discardRow, setDiscardRow] = useState(null);
  const [discardLoading, setDiscardLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { hasPermission } = useAuth();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const search = filterModel.quickFilterValues?.join(" ") || "";

      const response = await getUnlinkedLawsuits({
        page: paginationModel.page + 1,
        perPage: paginationModel.pageSize,
        search,
        review_status: "pending",
      });

      setRows(response.data.data || []);
      setTotal(response.data.total || 0);
    } catch {
      showToast("No se pudieron cargar los títulos no vinculados.", "error");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handlePreview = async (id) => {
    try {
      setPreviewLoading(true);
      const response = await downloadUnlinkedLawsuit(id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setOpenPreview(true);
    } catch {
      showToast("No se pudo cargar el PDF.", "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleEditOpen = (row) => {
    setSelectedRow(row);
    setEditData({
      type: row.type || "",
      order_number: row.order_number || "",
      identification: row.identification || "",
      decree: row.decree || "",
      dni_cuit: row.dni_cuit || "",
      first_name: row.first_name || "",
      last_name: row.last_name || "",
      person_type: row.person_type || "",
      amount: row.amount ?? "",
      date: row.date ? String(row.date).substring(0, 10) : "",
      contribution: row.contribution || "",
      address: row.address || "",
      reason: row.reason || "",
    });
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedRow(null);
  };

  const handleVincular = async () => {
    if (!selectedRow) return;

    try {
      setLinking(true);

      const payload = { ...editData };
      if (payload.amount === "") payload.amount = null;

      const updateRes = await updateUnlinkedLawsuit(selectedRow.id, payload);
      if (updateRes.data?.status !== "success") return;

      const completeRes = await completeUnlinkedLawsuit(selectedRow.id);

      if (completeRes.data?.status === "success") {
        showToast(completeRes.data.message, "success");
        handleCloseEdit();
        await fetchRows();
      }
    } catch (error) {
      const missing = error?.response?.data?.missing_fields;
      const message = missing?.length
        ? `Faltan campos obligatorios: ${missing
            .map((field) => REQUIRED_FIELD_LABELS[field] || field)
            .join(", ")}`
        : error?.response?.data?.message ||
          "No se pudo vincular el título.";
      showToast(message, "error");
    } finally {
      setLinking(false);
    }
  };

  const handleOpenDiscardConfirm = (row) => {
    setDiscardRow(row);
    setOpenDiscardConfirm(true);
  };

  const handleDiscard = async () => {
    if (!discardRow) return;

    try {
      setDiscardLoading(true);

      const res = await discardUnlinkedLawsuit(discardRow.id);

      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        setOpenDiscardConfirm(false);
        setDiscardRow(null);
        await fetchRows();
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || "No se pudo descartar el título.",
        "error",
      );
    } finally {
      setDiscardLoading(false);
    }
  };

  const columns = [
    {
      field: "original_name",
      headerName: "Archivo",
      flex: 1.4,
    },
    {
      field: "type",
      headerName: "Tipo",
      flex: 1,
      valueGetter: (value) => value || "—",
    },
    {
      field: "order_number",
      headerName: "Orden",
      flex: 0.8,
      valueGetter: (value) => value || "—",
    },
    {
      field: "identification",
      headerName: "Identificador",
      flex: 1,
      valueGetter: (value) => value || "—",
    },
    {
      field: "last_name",
      headerName: "Apellido/Razón Social",
      flex: 1,
      valueGetter: (value) => value || "—",
    },
    {
      field: "first_name",
      headerName: "Nombre",
      flex: 1,
      valueGetter: (value) => value || "—",
    },
    {
      field: "amount",
      headerName: "Monto",
      flex: 1,
      align: "right",
      valueFormatter: (value) =>
        value
          ? Number(value).toLocaleString("es-AR", {
              style: "currency",
              currency: "ARS",
            })
          : "—",
    },
    {
      field: "reason",
      headerName: "Motivo",
      flex: 1.6,
      valueGetter: (value) => value || "—",
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 1.6,
      sortable: false,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handlePreview(params.row.id)}
            disabled={!hasPermission("descargar demanda sin vincular")}
          >
            <VisibilityIcon />
          </IconButton>

          <IconButton
            color="success"
            onClick={() => handleEditOpen(params.row)}
            disabled={
              !hasPermission("actualizar demanda sin vincular") &&
              !hasPermission("completar demanda sin vincular")
            }
          >
            <LinkIcon />
          </IconButton>

          <IconButton
            color="error"
            onClick={() => handleOpenDiscardConfirm(params.row)}
            disabled={!hasPermission("descartar demanda sin vincular")}
          >
            <DeleteSweepIcon />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12 }}>
        <DatagridCustom
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          title="Títulos no vinculados"
          description="Títulos cuyo PDF no pudo procesarse automáticamente. Complete los campos faltantes para generar el registro definitivo."
          icon={<AssignmentLateRoundedIcon sx={{ fontSize: 32 }} />}
          paginationModel={paginationModel}
          onPaginationChange={setPaginationModel}
          filterModel={filterModel}
          onFilterChange={setFilterModel}
          filterMode="server"
          total={total}
          loading={loading}
          isServerPagination={true}
        />
      </Grid>

      <AppDialog
        open={openPreview}
        onClose={handleClosePreview}
        title="Vista previa del archivo"
        maxWidth="lg"
        fullScreenMobile
        fullScreenBreakpoint="md"
        stickyHeader
        contentSx={{ p: 0 }}
        paperSx={{ borderRadius: isMobile ? 0 : 4, overflow: "hidden" }}
        actions={
          !isMobile ? (
            <Button
              onClick={handleClosePreview}
              sx={{ borderRadius: 2, fontWeight: 800 }}
            >
              Cerrar
            </Button>
          ) : null
        }
      >
        {previewLoading ? (
          <Box sx={{ p: 2 }}>
            <Skeleton variant="rounded" height={isMobile ? 420 : 680} />
          </Box>
        ) : previewUrl ? (
          <iframe
            src={previewUrl}
            style={{
              width: "100%",
              height: isMobile ? "calc(100vh - 56px)" : "82vh",
              border: 0,
            }}
            title="preview"
          />
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography>No se pudo cargar el archivo.</Typography>
          </Box>
        )}
      </AppDialog>

      <EditDialog
        open={openEdit}
        onClose={handleCloseEdit}
        onSave={handleVincular}
        saveText={linking ? "Vinculando..." : "Vincular"}
        title={`Detalle de Título No Vinculado ${selectedRow ? `#${selectedRow.id}` : ""}`}
        subtitle="Complete los campos obligatorios (*) y presione Vincular para generar el título."
        values={{
          ...selectedRow,
          ...editData,
          created_at: selectedRow?.created_at
            ? new Date(selectedRow.created_at).toLocaleString("es-AR")
            : "",
        }}
        setValues={setEditData}
        saveDisabled={
          linking ||
          (!hasPermission("actualizar demanda sin vincular") &&
            !hasPermission("completar demanda sin vincular"))
        }
        sections={[
          {
            title: "Información general",
            description: "Datos solo de lectura.",
            icon: <AssignmentLateRoundedIcon />,
            fields: [
              {
                name: "original_name",
                label: "Archivo original",
                readOnly: true,
              },
              {
                name: "created_at",
                label: "Creado",
                readOnly: true,
              },
            ],
          },
          {
            title: "Datos para completar el título",
            description:
              "Tipo, orden, identificador, decreto, monto y fecha son obligatorios para generar el registro.",
            icon: <EditIcon />,
            fields: [
              {
                name: "type",
                label: "Tipo *",
                type: "select",
                options: TYPE_OPTIONS,
              },
              { name: "order_number", label: "Orden *" },
              { name: "identification", label: "Identificador *" },
              { name: "decree", label: "Decreto *" },
              { name: "amount", label: "Monto *", type: "number" },
              { name: "date", label: "Fecha *", type: "date" },
              { name: "dni_cuit", label: "DNI/CUIT" },
              {
                name: "person_type",
                label: "Tipo de persona",
                type: "select",
                options: PERSON_TYPE_OPTIONS,
              },
              { name: "last_name", label: "Apellido/Razón Social" },
              { name: "first_name", label: "Nombre" },
              { name: "contribution", label: "Contribución" },
              { name: "address", label: "Domicilio", md: 12 },
              {
                name: "reason",
                label: "Motivo",
                multiline: true,
                minRows: 2,
                md: 12,
              },
            ],
          },
        ]}
      />

      <ConfirmDialog
        open={openDiscardConfirm}
        onClose={() => {
          setOpenDiscardConfirm(false);
          setDiscardRow(null);
        }}
        onConfirm={handleDiscard}
        title="Confirmar descarte"
        description={`¿Seguro que deseas descartar el título "${discardRow?.original_name || ""}"?`}
        confirmText={discardLoading ? "Descartando..." : "Descartar"}
        cancelText="Cancelar"
        confirmColor="error"
        loading={discardLoading}
      />
    </Grid>
  );
}
